import { renderRichText } from "@storyblok/react";
import { storyblokEditable } from "@storyblok/react/rsc";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import { getUrlFromMultilink } from "@/lib/utils";
import type { Accordion as AccordionBlok } from "@/types/storyblok";
import { AccordionWithTracking } from "./AccordionWithTracking";

export default function AccordionComponent({ blok }: { blok: AccordionBlok }) {
  const subtitleLinkUrl = blok.subtitle_link
    ? getUrlFromMultilink(blok.subtitle_link)
    : null;
  const hasValidLink = subtitleLinkUrl && subtitleLinkUrl !== "#";

  return (
    <div
      className="relative"
      // biome-ignore lint/suspicious/noExplicitAny: Required for Storyblok React compatibility
      {...storyblokEditable(blok as any)}
    >
      <div className="max-w-screen-lg mx-auto px-4 sm:px-6 relative isolate pt-24 pb-48 sm:text-center">
        <div className="flex flex-col space-y-4 max-w-screen-lg">
          {blok.subtitle &&
            (hasValidLink ? (
              <Link
                href={subtitleLinkUrl}
                className="text-teal-500 flex items-center gap-2 sm:justify-center text-2xl drop-shadow-2xl font-medium font-display uppercase hover:text-teal-600 transition-colors duration-300"
              >
                {blok.subtitle} <ArrowRight />
              </Link>
            ) : (
              <span className="text-teal-500 text-2xl drop-shadow-2xl font-medium font-display uppercase">
                {blok.subtitle}
              </span>
            ))}
          {blok.title && (
            <h2 className="text-5xl md:text-6xl drop-shadow-2xl font-extrabold font-display bg-gradient-to-br from-violet-100 via-zinc-50 to-violet-200 bg-clip-text text-transparent leading-tight pb-1">
              {blok.title}
            </h2>
          )}
          {blok.lead && (
            <p className="text-xl drop-shadow-2xl max-w-prose font-medium text-muted-foreground mt-4 sm:mt-6 mb-8 mx-auto">
              {blok.lead}
            </p>
          )}
        </div>
        {blok.items && blok.items.length > 0 && (
          <div className="mt-12">
            <AccordionWithTracking
              items={blok.items.map((item) => ({
                _uid: item._uid,
                title: item.title,
                icon: item.icon,
                // @ts-expect-error - Storyblok richtext types are complex
                renderedContent: item.copy ? renderRichText(item.copy) : null,
              }))}
              sectionTitle={blok.title}
            />
          </div>
        )}
      </div>
    </div>
  );
}
